/**
 * Transcription Sample Playground — the primary way to try the speech-to-text APIs. Covers all
 * four supported flows against the same endpoints a third-party integrator calls:
 *
 *  1. Batch file      — upload an audio file, poll for the transcript.
 *  2. Recorded clip   — record in the browser, upload when you stop.
 *  3. Realtime file   — upload a file and watch partial transcripts stream in over the hub.
 *  4. Live mic        — stream the microphone and transcribe the conversation as it happens.
 *
 * Speech-to-text only: nothing here produces spoken replies.
 */

import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Button,
  Chip,
  CoordioDropdown,
  FormField,
  ShellHeader,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Input,
} from '@coordio/ui';
import { FormSection, PageContainer } from '@coordio/ui/layout';
import '../components/layouts/PageDetailLayout.scss';
import { Copy, ExternalLink, Mic, Square, Upload } from 'lucide-react';
import { agentService } from '../services/agentService';
import { transcriptionService } from '../services/transcriptionService';
import { transcriptionSignalRService } from '../services/transcriptionSignalRService';
import { useSignalR } from '../providers/SignalRProvider';
import { useAuthStore } from '../store/authStore';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import type { AgentDefinitionDto, AgentSpeechConfig } from '../types/agent';
import type { TranscriptionTransactionDto } from '../types/transcription';
import { ROUTES } from '../utils/routes';

const LANGUAGES = ['en-US', 'en-GB', 'en-IN', 'es-ES', 'fr-FR', 'de-DE', 'hi-IN', 'ja-JP'];

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

type TabKey = 'batch' | 'record' | 'realtimeFile' | 'liveMic';

/**
 * Maps raw provider speaker ids (Guest-1, Guest-2, Unknown) to display labels, numbering speakers
 * in first-seen order — the same rule the server uses for the persisted transcript.
 */
const speakerLabelFor = (speakerNumbers: Map<string, number>, speakerId?: string | null): string => {
  if (!speakerId || speakerId.toLowerCase() === 'unknown') {
    return 'Speaker';
  }
  let n = speakerNumbers.get(speakerId);
  if (!n) {
    n = speakerNumbers.size + 1;
    speakerNumbers.set(speakerId, n);
  }
  return `Speaker ${n}`;
};

/** Parses an agent's speechConfigJson, tolerating missing/malformed JSON. */
const parseSpeechConfig = (json?: string | null): AgentSpeechConfig | null => {
  if (!json?.trim()) return null;
  try {
    return JSON.parse(json) as AgentSpeechConfig;
  } catch {
    return null;
  }
};

/** One-line summary of an agent's Speech config, e.g. "en-US · diarization on · 3 phrases". */
const speechSummary = (config: AgentSpeechConfig | null): string => {
  const language = config?.language || 'en-US';
  const diarization = (config?.diarization ?? true) ? 'diarization on' : 'diarization off';
  const phraseCount = config?.phraseList?.phrases?.length ?? 0;
  const phrases = phraseCount === 1 ? '1 phrase' : `${phraseCount} phrases`;
  return `${language} · ${diarization} · ${phrases}`;
};

/** Renders transcript text, bolding `Speaker N:` prefixes so conversation turns read clearly. */
const TranscriptLines = ({ text }: { text: string }) => (
  <pre className="m-0 whitespace-pre-wrap text-sm">
    {text.split('\n').map((line, i) => {
      const match = line.match(/^(Speaker(?: \d+)?):\s?(.*)$/);
      return (
        <span key={i} className="block">
          {match ? (
            <>
              <span className="font-semibold">{match[1]}:</span> {match[2]}
            </>
          ) : (
            line
          )}
        </span>
      );
    })}
  </pre>
);

const TAB_DESCRIPTIONS: Record<TabKey, string> = {
  batch: 'Upload an audio file and poll POST /v1/audio/transcriptions until the transcript is ready. This is the flow most integrations use.',
  record: 'Record a clip in the browser, then upload it as a single batch job when you stop. Same endpoint as Batch file, with mode=batch_recording.',
  realtimeFile: 'Upload a file and watch partial transcripts arrive over the transcription hub while the server recognizes it.',
  liveMic: 'Transcribe your microphone as you speak. Recognition runs in your browser against Azure Speech using a short-lived token from the API; the transcript is reported to the hub, saved, and agent-corrected server-side. Nothing is spoken back — transcript only.',
};

/** Panel showing a live-updating transcript with a copy button. */
const TranscriptPanel = ({
  title,
  finalText,
  partialText,
  onCopy,
  height = 200,
}: {
  title: string;
  finalText: string;
  partialText?: string;
  onCopy: (text: string) => void;
  height?: number;
}) => (
  <div className="mt-2">
    <div className="mb-0.5 flex items-center justify-between">
      <span className="text-sm font-semibold">{title}</span>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button type="button" variant="ghost" size="icon" disabled={!finalText} onClick={() => onCopy(finalText)}>
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Copy transcript</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
    <div
      className="overflow-auto rounded border border-[var(--color-border-default)] bg-[var(--color-bg-subtle)] p-2"
      style={{ height }}
    >
      {finalText ? (
        <TranscriptLines text={finalText} />
      ) : (
        <span className="text-xs text-[var(--color-text-secondary)]">
          Transcript will appear here.
        </span>
      )}
      {partialText && (
        <p className="mt-1 text-sm italic text-[var(--color-text-secondary)]">
          {partialText}
        </p>
      )}
    </div>
  </div>
);

export const TranscriptionPlaygroundPage = () => {
  const navigate = useNavigate();
  const { hubBaseUrl } = useSignalR();
  const token = useAuthStore((s) => s.token);
  const { addNotification } = useNotificationStore();
  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);
  const hasContext = Boolean(currentTenant && currentProject);

  const [tab, setTab] = useState<TabKey>('batch');
  const [language, setLanguage] = useState('en-US');
  const [clientCorrelationId, setClientCorrelationId] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Agent scoping: None (default) keeps the unscoped /v1/audio API; picking an agent switches to
  // /v1/agents/{slug}/transcriptions so recognition uses the agent's Speech config and the
  // agent's prompt corrects the transcript at the end.
  const [agents, setAgents] = useState<AgentDefinitionDto[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<AgentDefinitionDto | null>(null);
  const agentSlug = selectedAgent?.slug;
  const selectedSpeechConfig = parseSpeechConfig(selectedAgent?.speechConfigJson);

  // Batch + recorded clip + realtime file share one result slot.
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<TranscriptionTransactionDto | null>(null);
  const [streamedFinal, setStreamedFinal] = useState('');
  const [streamedPartial, setStreamedPartial] = useState('');

  // Recording state (browser MediaRecorder).
  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);

  // Speaker id -> Speaker N assignment for the streaming tabs; reset per run.
  const speakerNumbersRef = useRef<Map<string, number>>(new Map());

  // Live mic state.
  const [micActive, setMicActive] = useState(false);
  const [micSession, setMicSession] = useState<{ transactionId: string; correlationId: string } | null>(null);
  const [micCorrectedText, setMicCorrectedText] = useState<string | null>(null);
  const [micCorrectionSessionId, setMicCorrectionSessionId] = useState<string | null>(null);
  // True while the server-side agent correction is still running after stop (it is backgrounded
  // so hour-long transcripts never block or overflow the hub invoke).
  const [micCorrecting, setMicCorrecting] = useState(false);

  // null = unknown (still loading); true/false once the server answered.
  const [speechConfigured, setSpeechConfigured] = useState<boolean | null>(null);

  useEffect(() => {
    transcriptionService
      .getConfigStatus()
      .then((status) => setSpeechConfigured(status.speechConfigured))
      .catch(() => setSpeechConfigured(null));

    return () => {
      void transcriptionSignalRService.disconnect();
    };
  }, []);

  // Active agents (including system agents) for the Agent picker; reload on context switch.
  useEffect(() => {
    if (!hasContext) return;
    setSelectedAgent(null);
    agentService
      .getAgents({ isActive: true, pageSize: 200 })
      .then((page) => setAgents(page.items))
      .catch(() => setAgents([]));
  }, [hasContext, currentTenant?.id, currentProject?.id]);

  // A hub session is bound to the tenant/project it was started with. After a switch, drop the
  // connection so the next use reconnects under the new context; a running mic session is
  // cancelled server-side on disconnect (its partial transcript is kept as Cancelled).
  useEffect(() => {
    void transcriptionSignalRService.disconnect();
    setMicActive(false);
  }, [currentTenant?.id, currentProject?.id]);

  const ensureHub = async () => {
    if (!token) throw new Error('Not authenticated.');
    // The hub requires the tenant as an invoke argument (Azure SignalR drops negotiate query params).
    if (!currentTenant?.id) throw new Error('Select a tenant before using live transcription.');

    transcriptionSignalRService.setEvents({
      onPartial: (data) =>
        setStreamedPartial(`${speakerLabelFor(speakerNumbersRef.current, data.speakerId)}: ${data.text}`),
      onFinal: (data) => {
        setStreamedPartial('');
        const line = `${speakerLabelFor(speakerNumbersRef.current, data.speakerId)}: ${data.text}`;
        setStreamedFinal((current) => (current ? `${current}\n${line}` : line));
      },
      onStatus: (data) => {
        if (data.status === 'Completed' || data.status === 'Failed') {
          setBusy(false);
        }
      },
      onError: (data) => {
        setError(`${data.errorCode}: ${data.errorMessage}`);
        setBusy(false);
        // Recognition was torn down client-side: a lost hub session cannot persist finals.
        if (data.errorCode === 'mic_session_lost' || data.errorCode === 'connection_closed') {
          setMicActive(false);
        }
      },
      // The hub connection is gone for good (reconnects exhausted); nothing is listening anymore.
      onConnectionClosed: () => setMicActive(false),
    });

    // The service reuses the connection when the tenant/project match and reconnects when they
    // changed, so hub invokes always carry the current context.
    await transcriptionSignalRService.connect(hubBaseUrl, token, currentTenant.id, currentProject?.id);
  };

  const copy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    addNotification('Transcript copied to clipboard', 'success');
  };

  const resetResults = () => {
    setError(null);
    setResult(null);
    setStreamedFinal('');
    setStreamedPartial('');
    setMicCorrectedText(null);
    setMicCorrectionSessionId(null);
    setMicCorrecting(false);
    speakerNumbersRef.current = new Map();
  };

  /** Polls the public GET endpoint (agent-scoped when a slug is given) until a terminal status. */
  const pollUntilDone = async (transactionId: string, slug?: string): Promise<TranscriptionTransactionDto> => {
    const deadline = Date.now() + POLL_TIMEOUT_MS;

    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      const current = await transcriptionService.getPublicTransaction(transactionId, slug);

      if (current.status === 'Completed' || current.status === 'Failed' || current.status === 'Cancelled') {
        return current;
      }
    }

    throw new Error('Timed out waiting for the transcript.');
  };

  const submitBatch = async (blob: Blob, fileName: string, mode: 'batch' | 'batch_recording') => {
    resetResults();
    setBusy(true);
    try {
      const accepted = await transcriptionService.createTranscription(blob, fileName, {
        mode,
        language,
        diarization: true,
        clientCorrelationId: clientCorrelationId || undefined,
        agentSlug,
      });
      setResult(accepted);

      const finished = await pollUntilDone(accepted.id, agentSlug);
      setResult(finished);

      if (finished.status === 'Failed') {
        setError(`${finished.errorCode}: ${finished.errorMessage}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Transcription failed');
    } finally {
      setBusy(false);
    }
  };

  const submitRealtimeFile = async (blob: Blob, fileName: string) => {
    resetResults();
    setBusy(true);
    try {
      await ensureHub();

      const accepted = await transcriptionService.createTranscription(blob, fileName, {
        mode: 'realtime_file',
        language,
        diarization: true,
        clientCorrelationId: clientCorrelationId || undefined,
        agentSlug,
      });
      setResult(accepted);

      // Subscribe before recognition gets far so early partials are not missed.
      await transcriptionSignalRService.watchTransaction(accepted.id);

      const finished = await pollUntilDone(accepted.id, agentSlug);
      setResult(finished);

      if (finished.status === 'Failed') {
        setError(`${finished.errorCode}: ${finished.errorMessage}`);
      } else if (finished.text) {
        setStreamedFinal(finished.text);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Realtime transcription failed');
    } finally {
      setBusy(false);
    }
  };

  const startRecording = async () => {
    resetResults();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordedChunksRef.current = [];

      const recorder = new MediaRecorder(stream);
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) recordedChunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(recordedChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        void submitBatch(blob, `recording-${Date.now()}.webm`, 'batch_recording');
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
    } catch (err: any) {
      setError(err.message || 'Microphone access was denied.');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    mediaRecorderRef.current = null;
    setRecording(false);
  };

  const startMic = async () => {
    resetResults();
    try {
      await ensureHub();
      const session = await transcriptionSignalRService.startMicSession(
        language,
        clientCorrelationId || undefined,
        agentSlug,
      );
      setMicSession(session);
      setMicActive(true);
    } catch (err: any) {
      setError(err.message || 'Failed to start the microphone session.');
    }
  };

  /**
   * Polls the transaction until the background agent correction finishes: correctedText appears
   * on success, a correction_failed event on failure. Timing out just leaves the raw transcript.
   */
  const pollMicCorrection = async (transactionId: string) => {
    setMicCorrecting(true);
    try {
      const deadline = Date.now() + POLL_TIMEOUT_MS;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        try {
          const current = await transcriptionService.getTransaction(transactionId);
          if (current.correctionSessionId) {
            setMicCorrectionSessionId(current.correctionSessionId);
          }
          if (current.correctedText) {
            setMicCorrectedText(current.correctedText);
            return;
          }
          if (current.events.some((event) => event.eventType === 'correction_failed')) {
            return;
          }
        } catch {
          // Transient fetch failure; keep polling until the deadline.
        }
      }
    } finally {
      setMicCorrecting(false);
    }
  };

  /**
   * Fetches the persisted transcript over REST (the hub returns ids only — an hour-long
   * transcript exceeds SignalR message buffers) and kicks off correction polling.
   */
  const showSavedMicTranscript = async (transactionId: string) => {
    const saved = await transcriptionService.getTransaction(transactionId);
    const text = saved.text ?? '';
    setStreamedFinal(text);

    if (text.trim()) {
      addNotification('Live transcription saved', 'success');
    } else {
      addNotification('Session saved, but no speech was recognized — check your microphone and try again.', 'info');
    }

    // Agent correction runs in the background server-side; poll until it lands.
    if (agentSlug && text.trim()) {
      void pollMicCorrection(transactionId);
    }
  };

  const stopMic = async () => {
    try {
      const finished = await transcriptionSignalRService.stopMicSession();
      setStreamedPartial('');
      await showSavedMicTranscript(finished.transactionId);
    } catch (err: any) {
      // The hub session can be gone (dropped connection, server recycle) while the transaction
      // row survived; recover the saved transcript by id instead of surfacing the hub error.
      if (micSession) {
        try {
          setStreamedPartial('');
          await showSavedMicTranscript(micSession.transactionId);
          return;
        } catch {
          // REST recovery failed too; report the original stop error below.
        }
      }
      setError(err.message || 'Failed to stop the microphone session.');
    } finally {
      setMicActive(false);
    }
  };

  const openTransaction = (transactionId: string) =>
    navigate(ROUTES.TRANSCRIPTION_DETAIL.replace(':id', transactionId));

  // Agent mode finished with a raw transcript but no corrected text — correction failed server-side.
  const correctionFailed =
    Boolean(agentSlug) && result?.status === 'Completed' && Boolean(result?.text) && !result?.correctedText;

  /** Result blocks for the batch-style tabs: corrected first in agent mode, raw as today otherwise. */
  const batchResultPanels = agentSlug ? (
    <>
      {correctionFailed && (
        <div className="mt-2 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-subtle)] p-3 text-sm">
          Correction failed; raw transcript was saved.
        </div>
      )}
      {result?.correctedText && (
        <TranscriptPanel title="Corrected transcript" finalText={result.correctedText} onCopy={copy} />
      )}
      <TranscriptPanel
        title="Raw speech-to-text"
        finalText={result?.text ?? ''}
        onCopy={copy}
        height={result?.correctedText ? 140 : 200}
      />
    </>
  ) : (
    <TranscriptPanel title="Transcript" finalText={result?.text ?? ''} onCopy={copy} />
  );

  if (!hasContext) {
    return (
      <div className="transcription-playground" data-testid="transcription-playground-detail-page">
        <ShellHeader
          title="Transcription Sample Playground"
          description="Try every speech-to-text flow against the same public API your integrations call."
        />
        <PageContainer>
          <div className="rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-subtle)] p-3 text-sm">
            Select a tenant and project (using the switchers above) to use the transcription playground.
          </div>
        </PageContainer>
      </div>
    );
  }

  const activeTransactionId = result?.id ?? micSession?.transactionId;
  const activeCorrectionSessionId = result?.correctionSessionId ?? micCorrectionSessionId;

  return (
    <div className="transcription-playground" data-testid="transcription-playground-detail-page">
      <ShellHeader
        title="Transcription Sample Playground"
        description="Try every speech-to-text flow against the same public API your integrations call. Transcript only — no spoken replies."
      />

      <PageContainer>
      <div className="flex flex-col gap-5">
      {speechConfigured === false && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
          Azure Speech is not configured on the server, so transcription requests will fail. Ask an
          administrator to set <code>AzureSpeech:Key</code> and <code>AzureSpeech:Region</code> (or{' '}
          <code>AzureSpeech:Endpoint</code>) in the API configuration, or the <code>SPEECH_KEY</code> /{' '}
          <code>SPEECH_REGION</code> environment variables, then restart the API.
        </div>
      )}

      <FormSection title="Configuration" description="Select agent scope, language, and optional correlation id." data-testid="transcription-section-config">
        <div className="grid gap-5 items-end sm:grid-cols-3">
          <FormField label="Agent" description="Scope to an agent for prompt correction, or leave unscoped.">
            <CoordioDropdown
              options={agents.map((agent) => ({
                value: agent.id,
                label: `${agent.name} (${agent.slug})`,
              }))}
              value={selectedAgent?.id ?? ''}
              onChange={(val) => {
                const agent = agents.find((a) => a.id === val) ?? null;
                setSelectedAgent(agent);
                const configured = parseSpeechConfig(agent?.speechConfigJson)?.language;
                if (configured) setLanguage(configured);
              }}
              placeholder="None — unscoped API"
              clearable
            />
          </FormField>
          <FormField label="Language" description="Recognition language code.">
            <CoordioDropdown
              options={LANGUAGES.map((code) => ({ value: code, label: code }))}
              value={language}
              onChange={(val) => setLanguage(val ?? 'en-US')}
              clearable={false}
            />
          </FormField>
          <FormField label="Correlation ID" description="Sent as X-Correlation-Id so you can find this job later.">
            <Input
              placeholder="e.g. order-4711"
              value={clientCorrelationId}
              onChange={(e) => setClientCorrelationId(e.target.value)}
            />
          </FormField>
        </div>
        {selectedAgent && (
          <div className="mt-3 flex flex-wrap items-center gap-1">
            <span className="text-xs text-[var(--color-text-secondary)]">
              Speech: {speechSummary(selectedSpeechConfig)} · runs prompt correction on the final transcript ·
            </span>
            <RouterLink
              to={ROUTES.AGENT_EDIT.replace(':id', selectedAgent.id)}
              className="text-xs text-[var(--color-text-link)] hover:underline"
            >
              Edit agent Speech
            </RouterLink>
          </div>
        )}
      </FormSection>

      <div className="rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)]">
        <div className="page-detail-layout__tabs" role="tablist" aria-label="Transcription modes">
          {([
            { key: 'batch' as TabKey, label: 'Batch file' },
            { key: 'record' as TabKey, label: 'Record then upload' },
            { key: 'realtimeFile' as TabKey, label: 'Realtime file' },
            { key: 'liveMic' as TabKey, label: 'Live mic' },
          ] as const).map(({ key, label }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className="page-detail-layout__tab"
              onClick={() => { setTab(key); resetResults(); }}
              data-testid={`transcription-playground-tab-${key}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="p-3">
          <div className="mb-3 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-subtle)] p-3 text-sm">
            {TAB_DESCRIPTIONS[tab]}
          </div>

          {error && (
            <div className="mb-3 flex items-start justify-between rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-700 dark:bg-red-950 dark:text-red-200">
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)} className="ml-2 shrink-0 text-red-500 hover:text-red-700">✕</button>
            </div>
          )}

          {tab === 'batch' && (
            <>
              <Button type="button" variant="default" disabled={busy} className="gap-2 relative" onClick={() => (document.getElementById('batch-audio-input') as HTMLInputElement)?.click()}>
                <Upload className="h-4 w-4" /> Choose audio file
                <input
                  id="batch-audio-input"
                  className="hidden"
                  type="file"
                  accept="audio/*,video/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void submitBatch(file, file.name, 'batch');
                    e.target.value = '';
                  }}
                />
              </Button>
              {busy && <div className="mt-2 h-1 w-full overflow-hidden rounded bg-[var(--color-bg-subtle)]"><div className="h-full animate-pulse rounded bg-[var(--color-accent)]" style={{ width: '60%' }} /></div>}
              {batchResultPanels}
            </>
          )}

          {tab === 'record' && (
            <>
              <Button
                type="button"
                variant={recording ? 'destructive' : 'default'}
                className="gap-2"
                onClick={recording ? stopRecording : startRecording}
                disabled={busy && !recording}
              >
                {recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                {recording ? 'Stop and transcribe' : 'Start recording'}
              </Button>
              {recording && (
                <Chip label="Recording…" color="red" className="ml-2" />
              )}
              {busy && <div className="mt-2 h-1 w-full overflow-hidden rounded bg-[var(--color-bg-subtle)]"><div className="h-full animate-pulse rounded bg-[var(--color-accent)]" style={{ width: '60%' }} /></div>}
              {batchResultPanels}
            </>
          )}

          {tab === 'realtimeFile' && (
            <>
              <Button type="button" variant="default" disabled={busy} className="gap-2 relative" onClick={() => (document.getElementById('realtime-audio-input') as HTMLInputElement)?.click()}>
                <Upload className="h-4 w-4" /> Choose audio file
                <input
                  id="realtime-audio-input"
                  className="hidden"
                  type="file"
                  accept="audio/*,video/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void submitRealtimeFile(file, file.name);
                    e.target.value = '';
                  }}
                />
              </Button>
              {busy && (
                <div className="mt-2 flex items-center gap-1">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-[var(--color-accent)] border-t-transparent" />
                  <span className="text-xs text-[var(--color-text-secondary)]">
                    Streaming partial results…
                  </span>
                </div>
              )}
              {correctionFailed && (
                <div className="mt-2 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-subtle)] p-3 text-sm">
                  Correction failed; raw transcript was saved.
                </div>
              )}
              {result?.correctedText && (
                <TranscriptPanel title="Corrected transcript" finalText={result.correctedText} onCopy={copy} />
              )}
              <TranscriptPanel
                title={agentSlug ? 'Raw speech-to-text (streamed)' : 'Streaming transcript'}
                finalText={streamedFinal}
                partialText={streamedPartial}
                onCopy={copy}
                height={result?.correctedText ? 160 : 260}
              />
            </>
          )}

          {tab === 'liveMic' && (
            <>
              <Button
                type="button"
                variant={micActive ? 'destructive' : 'default'}
                className="gap-2"
                onClick={micActive ? stopMic : startMic}
              >
                {micActive ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                {micActive ? 'Stop listening' : 'Start listening'}
              </Button>
              {micActive && <Chip label="Listening…" color="red" className="ml-2" />}
              {micCorrecting && (
                <div className="mt-2 flex items-center gap-1">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-[var(--color-accent)] border-t-transparent" />
                  <span className="text-xs text-[var(--color-text-secondary)]">
                    Running agent correction…
                  </span>
                </div>
              )}
              {agentSlug && !micActive && !micCorrecting && streamedFinal && !micCorrectedText && (
                <div className="mt-2 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-subtle)] p-3 text-sm">
                  Correction failed; raw transcript was saved.
                </div>
              )}
              {micCorrectedText && (
                <TranscriptPanel title="Corrected transcript" finalText={micCorrectedText} onCopy={copy} />
              )}
              <TranscriptPanel
                title={agentSlug ? 'Raw speech-to-text (live)' : 'Live transcript'}
                finalText={streamedFinal}
                partialText={streamedPartial}
                onCopy={copy}
                height={micCorrectedText ? 160 : 260}
              />
            </>
          )}

          {activeTransactionId && (
            <div className="mt-3 flex flex-wrap items-center gap-1">
              <span className="text-xs text-[var(--color-text-secondary)]">
                Correlation id:
              </span>
              <span className="font-mono text-xs">
                {result?.correlationId ?? micSession?.correlationId}
              </span>
              <Button type="button" variant="ghost" size="sm" className="gap-1" onClick={() => openTransaction(activeTransactionId)}>
                Open transaction & event log <ExternalLink className="h-3 w-3" />
              </Button>
              {activeCorrectionSessionId && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="gap-1"
                  onClick={() => navigate(ROUTES.SESSION_DETAIL.replace(':id', activeCorrectionSessionId))}
                >
                  Open correction session <ExternalLink className="h-3 w-3" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      </div>
      </PageContainer>
    </div>
  );
};
